import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";

export type ImageRatio =
  | "1:1"
  | "2:3"
  | "3:2"
  | "16:9"
  | "4:3"
  | "9:16"
  | "Custom";

export interface ImageProps extends ThemeOverrideProps {
  /** Image source URL */
  src?: string;
  /** Image alt text */
  alt?: string;
  /** Aspect ratio of the image. Defaults to '1:1' */
  ratio?: ImageRatio;
  /** Custom aspect ratio as a string (e.g. '21 / 9') or number (e.g. 2.33) if ratio is set to 'Custom' */
  customRatio?: string | number;
  /** Optional slot for overlay or placeholder content */
  slot?: React.ReactNode;
  /** Width of the image container. Defaults to '100%' */
  width?: number | string;
  /** Border radius of the image container. Defaults to 0 */
  borderRadius?: number | string;
  /** Style for the container Box */
  style?: React.CSSProperties;
  /** Style for the img element */
  imageStyle?: React.CSSProperties;
  testID?: string;
}

const ratioMap: Record<Exclude<ImageRatio, "Custom">, string> = {
  "1:1": "1 / 1",
  "2:3": "2 / 3",
  "3:2": "3 / 2",
  "16:9": "16 / 9",
  "4:3": "4 / 3",
  "9:16": "9 / 16",
};

/**
 * Image component with support for various aspect ratios and overlays.
 */
export const Image: React.FC<ImageProps> = ({
  src,
  alt = "",
  ratio = "1:1",
  customRatio,
  slot,
  width = "100%",
  borderRadius = 0,
  style,
  imageStyle,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const aspectRatio = ratio === "Custom" ? customRatio : ratioMap[ratio];

  return (
    <Box
      testID={testID}
      width={width}
      position="relative"
      overflow="hidden"
      borderRadius={borderRadius}
      backgroundColor={theme.colors.background.secondary}
      style={{
        ...style,
        aspectRatio: aspectRatio as any,
        display: "block",
      }}
    >
      {src && (
        <img
          src={src}
          alt={alt}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            ...imageStyle,
          }}
        />
      )}
      {slot && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          alignItems="center"
          justifyContent="center"
          zIndex={1}
        >
          {slot}
        </Box>
      )}
    </Box>
  );
};
